/**
 * Друкована форма ДОКУМЕНТА «Штатний розпис» (2026-08-13, рішення власника).
 * Друкує склад конкретного документа (на відміну від /staff-schedule/print —
 * зрізу на дату): підрозділ / посада / штатні одиниці / оклад / місячний ФОТ.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { calcTypesApi, departmentsApi, positionsApi, staffSchedules } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function StaffScheduleDocPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['staff-schedule', id],
    queryFn: () => staffSchedules.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgsQ.data?.items.find((o) => o.id === d?.organization_id);
  const depQ = useQuery({
    queryKey: ['departments', d?.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: d!.organization_id, limit: 1000 }),
    enabled: !!d?.organization_id,
  });
  const posQ = useQuery({
    queryKey: ['positions-print'],
    queryFn: () => positionsApi.list({ limit: 1000 }),
  });
  const calcQ = useQuery({ queryKey: ['calc-types'], queryFn: calcTypesApi.list });

  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;

  const depName = new Map((depQ.data?.items ?? []).map((x) => [x.id, x.name]));
  const posName = new Map((posQ.data?.items ?? []).map((x) => [x.id, x.name]));
  // Погодинні (HOURLY): salary = ставка ЗА ГОДИНУ. Плановий місячний фонд
  // (рішення власника 2026-08-13) = ставка × НОРМА ГОДИН місяця документа
  // (робочі дні пн–пт × 8 год, стандартний 40-год тиждень) × к-сть одиниць.
  const calcById = new Map((calcQ.data ?? []).map((c) => [c.id, c]));
  const isHourly = (it: { calc_type_id?: string | null }) =>
    it.calc_type_id ? calcById.get(it.calc_type_id)?.code === 'HOURLY' : false;
  const docDate = new Date(d.document_date);
  const normY = docDate.getFullYear();
  const normM = docDate.getMonth();           // 0-based
  const daysIn = new Date(normY, normM + 1, 0).getDate();
  let workDays = 0;
  for (let day = 1; day <= daysIn; day++) {
    const wd = new Date(normY, normM, day).getDay();
    if (wd !== 0 && wd !== 6) workDays++;
  }
  const normHours = workDays * 8;
  const lineFot = (it: { calc_type_id?: string | null;
                         rate_count: string | number; salary: string | number }) =>
    isHourly(it)
      ? Number(it.rate_count) * Number(it.salary) * normHours
      : Number(it.rate_count) * Number(it.salary);
  const totalRates = d.items.reduce((s, it) => s + Number(it.rate_count), 0);
  const totalFot = d.items.reduce((s, it) => s + lineFot(it), 0);
  const hourlyCount = d.items.filter(isHourly).length;

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .ss-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9.5pt; line-height: 1.2; }
        .ss-table th, .ss-table td { border: 1px solid #000; padding: 2px 5px; }
        .ss-table th { text-align: center; font-weight: 600; font-size: 8.5pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.staff_schedules',
        documentId: d.id,
        filename: `Штатний розпис ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0 0.25rem' }}>
        ШТАТНИЙ РОЗПИС № {d.document_number}
      </h2>
      <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '10pt', color: '#555' }}>
        від {new Date(d.document_date).toLocaleDateString('uk-UA')}
        {d.kind === 'CHANGE' && ' (зміни до штатного розпису)'}
      </div>

      <table className="ss-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Структурний підрозділ</th>
            <th>Посада</th>
            <th style={{ width: 80 }}>К-сть штатних одиниць</th>
            <th style={{ width: 110 }}>Оклад / тариф, грн</th>
            <th style={{ width: 110 }}>Місячний фонд, грн</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'center' }}>{i + 1}</td>
              <td>{it.department_id ? (depName.get(it.department_id) ?? '—') : '—'}</td>
              <td>{posName.get(it.position_id) ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{Number(it.rate_count)}</td>
              <td style={{ textAlign: 'right' }}>
                {fmt(it.salary)}{isHourly(it) ? '/год' : ''}
              </td>
              <td style={{ textAlign: 'right' }}
                  title={isHourly(it)
                    ? `Погодинна: ${fmt(it.salary)} грн/год × ${normHours} год (норма місяця)`
                    : undefined}>
                {fmt(lineFot(it))}{isHourly(it) ? '*' : ''}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'center' }}>{totalRates}</td>
            <td></td>
            <td style={{ textAlign: 'right' }}>{fmt(totalFot)}</td>
          </tr>
        </tfoot>
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Штатний розпис містить {d.items.length} позицій,{' '}
        {totalRates} штатних одиниць. Плановий місячний фонд оплати праці —{' '}
        <strong>{fmt(totalFot)} грн</strong>.
        {hourlyCount > 0 && (
          <> * Погодинні позиції ({hourlyCount}) — плановий фонд за нормою{' '}
          {new Date(d.document_date).toLocaleDateString('uk-UA', { month: 'long', year: 'numeric' })}:{' '}
          {workDays} роб. днів × 8 год = {normHours} год; фактична оплата — за
          відпрацьованими годинами.</>
        )}
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Керівник</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Головний бухгалтер</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
